import { DateTime, DateTimeFormatOptions, Interval } from 'luxon';

const dateFormatConfigs = {
  Full: {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    weekday: 'long',
  },
  Default: {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  },
  Dayname: { weekday: 'long' },
  LongMonth: {
    month: 'long',
    day: '2-digit',
    year: 'numeric',
  },
  Extended: {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    weekday: 'long',
  },
  DateAndTime: {
    hour: '2-digit',
    minute: '2-digit',
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  },
  Time: {
    hour: '2-digit',
    minute: '2-digit',
  },
  DayAndTime: {
    hour: '2-digit',
    minute: '2-digit',
    weekday: 'long',
  },
  DayAndMonth: {
    day: '2-digit',
    month: '2-digit',
  },
  Year: {
    year: 'numeric',
  },
  Monthname: {
    month: 'long',
  },
  Month: {
    month: 'numeric',
  },
};
export function getDateFormat(name: string): DateTimeFormatOptions {
  return dateFormatConfigs[name];
}

const dateFormatStrings = {
  Full: 'EEEE dd.MM.yyyy',
  Default: 'dd.MM.yyyy',
  Dayname: 'EEEE',
  ISODashed: 'yyyy-MM-dd',
  Extended: 'EEEE[,] dd.MM.yyyy',
  ExtendedAlt: ' dd.MM. [(]EEEE[)]',
  DateAndTime: 'dd.MM.yyyy HH:mm',
  DayAndTime: 'EEEE HH:mm',
  Time: 'HH:mm',
  Year: 'yyyy',
  Monthname: 'MMMM',
};
export function getDateParseFormat(name: string): string {
  return dateFormatStrings[name];
}

const weekdayNames = [
  'Montag',
  'Dienstag',
  'Mittwoch',
  'Donnerstag',
  'Freitag',
  'Samstag',
  'Sonntag',
];

export function getWeekDayName(weekday: number) {
  return weekdayNames[weekday];
}
export function splitTimeString(time: string): number[] {
  const splits = time.split(':');
  return [Number.parseInt(splits[0]), Number.parseInt(splits[1])];
}

export function toISODate(timestamp) {
  return DateTime.fromMillis(timestamp).toISODate() || 0;
}

export function fromUTCString(date): DateTime {
  return DateTime.fromISO(`${date}`);
}

export function formatDate(
  timestamp: number | string | Date | DateTime | null,
  format: string
): string {
  if (!timestamp) return '';

  const dt =
    typeof timestamp === 'number'
      ? DateTime.fromMillis(timestamp, { zone: 'Europe/Berlin' })
      : typeof timestamp === 'string'
        ? DateTime.fromISO(timestamp, { zone: 'Europe/Berlin' })
        : timestamp instanceof Date
          ? DateTime.fromMillis(timestamp.getTime(), { zone: 'Europe/Berlin' })
          : DateTime.now().setZone('Europe/Berlin');

  dt.setZone('Europe/Berlin');
  const opts: DateTimeFormatOptions = dateFormatConfigs[format];
  return dt.toLocaleString(opts, { locale: 'de-DE' });
}

export function toLocaleString(date: DateTime, format, options: object) {
  const fullOptions = {
    ...options,
    locale: 'de-DE',
  };
  return date?.toLocaleString(format, fullOptions);
}

export function getNow(): DateTime {
  return DateTime.now().setZone('Europe/Berlin');
}

export function getDay(timestamp): string | null {
  return DateTime.fromMillis(timestamp).toISODate();
}
export function fromDay(dateString): DateTime {
  return DateTime.fromFormat(dateString, 'yyyy-MM-dd');
}
export function getYesterday(): DateTime {
  return getNow().minus({ days: 1 });
}

export function getTimeDiff(start: DateTime, end: DateTime) {
  const diff = Interval.fromDateTimes(start, end);
  const hours = Math.floor(diff.length('hours'));
  const minutes = Math.floor(diff.length('minutes') - hours * 60);
  const seconds = Math.floor(
    diff.length('seconds') - hours * 60 * 60 - minutes * 60
  );
  return {
    hours,
    minutes,
    seconds,
  };
}

export function fromTimestamp(time: number): DateTime {
  try {
    if (!time) return DateTime.now();
    return typeof time === 'object'
      ? time
      : DateTime.fromMillis(time).setZone('Europe/Berlin');
  } catch {}
  return DateTime.now();
}

export function parse(input: string, format: string): DateTime {
  return DateTime.fromFormat(input, format).setZone('Europe/Berlin');
}
